/** English strings (single `en` namespace). Keys are grouped by surface. */
export const en = {
  brand: { name: 'Title21', product: 'Support Desk' },
  common: {
    cancel: 'Cancel',
    save: 'Save',
    saving: 'Saving…',
    create: 'Create',
    delete: 'Delete',
    edit: 'Edit',
    close: 'Close',
    dismiss: 'Dismiss',
    retry: 'Retry',
    loading: 'Loading…',
    none: 'None',
    any: 'any',
    all: 'all',
    yes: 'Yes',
    no: 'No',
    optional: 'optional',
    required: 'Required',
    select: 'Select…',
    clear: 'Clear',
    error: 'Something went wrong: {{message}}',
    notAvailable: 'Not available yet: the API does not serve this endpoint.',
    forbidden: 'You do not have permission to see this.',
  },
  nav: {
    sidebar: 'Main navigation',
    collapseSidebar: 'Collapse sidebar',
    expandSidebar: 'Expand sidebar',
    starred: 'Starred',
    teamFolder: 'team folder',
    myFolder: 'my folder',
    folderEmpty: 'No boards yet',
    manageBoards: 'New board or folder',
    queues: 'Queues',
    byProduct: 'By product',
    productNav: {
      customize: 'Customize By product',
      title: 'Customize By product',
      help: 'Only for you. Drag or use the arrows to reorder, pick an icon, hide products you don’t work on. New products appear at the end.',
      pickIcon: 'Icon for {{name}}',
      icons: 'Icons',
      noIcon: 'No icon',
      up: 'Move {{name}} up',
      down: 'Move {{name}} down',
      hide: 'Hide {{name}}',
      show: 'Show {{name}}',
      reset: 'Reset to default',
      allHidden: 'All products are hidden.',
      iconNames: {
        dna: 'DNA',
        flask: 'Flask',
        microscope: 'Microscope',
        'test-tube': 'Test tube',
        syringe: 'Syringe',
        'heart-pulse': 'Heart pulse',
        'shield-check': 'Shield',
        'clipboard-check': 'Checklist',
        'file-text': 'Document',
        'graduation-cap': 'Training',
        plug: 'Plug',
        server: 'Server',
        key: 'Key',
        boxes: 'Boxes',
        factory: 'Factory',
        truck: 'Truck',
        star: 'Star',
        folder: 'Folder',
      },
    },
    admin: 'Admin',
    allOpen: 'All open',
    myOrgTickets: 'Open tickets',
    assignedToMe: 'Assigned to me',
    slaAtRisk: 'SLA at risk',
    gxpImpacting: 'GxP-impacting',
    escalated: 'Escalated to engineering',
    analytics: 'Analytics',
    slaPolicies: 'SLA policies & routing',
    catalogProducts: 'Products',
    catalogClients: 'Clients & tenants',
    access: 'Users & roles',
    boards: 'Boards',
    allBoards: 'All boards',
    board: 'Board',
    signOut: 'Sign out',
    account: 'Account: {{name}}',
    breadcrumb: 'Breadcrumb',
    tickets: 'Tickets',
    logTicket: 'Log ticket',
    newTicket: 'New ticket',
  },
  user: {
    agent: 'Agent',
    superAdmin: 'Administrator',
    customer: 'Customer',
    tier: 'Tier {{tier}}',
  },
  realtime: {
    mention: 'You were mentioned on {{key}}',
    aTicket: 'a ticket',
    slaWarning: 'SLA warning on {{key}}',
  },
  queue: {
    view: { cards: 'Cards', table: 'Table' },
    eyebrow: 'Support desk · All customers · All products',
    customerEyebrow: 'Support · {{customer}}',
    customerEyebrowFallback: 'Support · Your organisation',
    title: 'All open tickets',
    titles: {
      all: 'All open tickets',
      mine: 'Assigned to me',
      sla_risk: 'SLA at risk',
      gxp: 'GxP-impacting tickets',
      escalated: 'Escalated to engineering',
    },
    customerTitle: 'Your tickets',
    productTitle: '{{product}} tickets',
    searchPlaceholder: 'Search ticket, customer, record…',
    searchLabel: 'Search tickets',
    newTicket: 'New ticket',
    kpi: {
      open: 'Open',
      critical: 'Critical (P1)',
      slaMet: 'SLA met · 30 days',
      medianFirstResponse: 'Median first response',
      escalated: 'Escalated to engineering',
      analyticsOnly: 'Available with Analytics',
    },
    chips: {
      all: 'All',
      priority: 'Priority',
      product: 'Product',
      type: 'Type',
      customer: 'Customer',
      tenant: 'Tenant',
      clear: 'Clear',
    },
    sortedBySla: 'Sorted by SLA remaining',
    columns: {
      id: 'ID',
      subject: 'Subject',
      customer: 'Customer',
      product: 'Product',
      status: 'Status',
      assignee: 'Assignee',
      sla: 'SLA',
    },
    unassigned: 'Unassigned',
    showing: 'Showing {{count}} of {{total}} · every change is captured in the Part 11 audit trail',
    nextPage: 'Next page →',
    previousPage: '← Previous',
    emptyTitle: 'No matching tickets',
    empty: 'No tickets match these filters.',
    emptyQueueTitle: 'All clear',
    emptyQueue: 'Nothing in this queue. Nice work.',
    clearFilters: 'Clear filters',
    loadError: 'The queue could not be loaded: {{message}}',
    slaPaused: 'SLA clock paused',
    slaPausedLabel: 'Paused',
    slaPausedLeft: 'SLA clock paused · {{time}} left',
    slaBreached: 'SLA breached',
  },
  newTicket: {
    title: 'New ticket',
    description: 'Desk-entered ticket. The reporter is a contact at the customer.',
    customer: 'Customer',
    environment: 'Environment',
    noEnvironment: 'No environment',
    reporter: 'Reporter',
    tenant: 'Tenant',
    product: 'Product',
    component: 'Component',
    noComponent: 'No component',
    type: 'Type',
    priority: 'Priority',
    slaHint: '{{policy}} · first response {{fr}}, resolution {{res}}',
    subject: 'Subject',
    descriptionLabel: 'Description',
    linkedRecord: 'Linked record',
    addLinkedRecord: 'Add a linked record',
    submit: 'Create ticket',
    submitting: 'Creating…',
    pickCustomerFirst: 'Pick a customer first',
    pickProductFirst: 'Pick a product first',
    fullForm: 'Open the full form',
    fullFormHint: 'Phone call, attachments or an internal note? Use the full form.',
    errors: {
      customer: 'Choose the customer.',
      reporter: 'Choose who reported it.',
      product: 'Choose the product.',
      subject: 'Add a subject.',
      description: 'Describe the problem.',
      record: 'A linked record needs a type and a key.',
    },
  },
  manualTicket: {
    eyebrowAgent: 'Tickets · Desk intake',
    eyebrowCustomer: 'Tickets · {{customer}}',
    titleAgent: 'Log a ticket',
    titleCustomer: 'New ticket',
    introAgent: 'For a request that came in by phone, email, Teams or in person. Every field is captured in the Part 11 audit trail.',
    introCustomer: 'Tell us what happened. Our support team is notified as soon as you submit.',
    backToQueue: '← Back to the queue',
    quickCreate: 'Quick create',
    sections: {
      reporter: 'Who reported it',
      intake: 'How it came in',
      about: 'What it is about',
      gxp: 'GxP and records',
      people: 'Owner and watchers',
      attachments: 'Attachments',
      note: 'Opening internal note',
    },
    customer: 'Customer',
    environment: 'Environment',
    noEnvironment: 'Not environment-specific',
    pickCustomerFirst: 'Pick a customer first',
    pickProductFirst: 'Pick a product first',
    reporterMode: 'Reporter',
    existingContact: 'Existing contact',
    newContact: 'New contact',
    contact: 'Contact',
    contactName: 'Name',
    contactEmail: 'Email',
    contactRole: 'Role',
    contactRolePlaceholder: 'QA Release Officer',
    newContactHelp: 'Matched by email if this contact already exists, otherwise created for {{customer}}.',
    newContactRoleNote: 'Saved on the new contact.',
    tenant: 'Tenant',
    tenantHelp: 'The client’s branch or site. Defaults to the reporter’s.',
    tenantFromReporter: 'From the reporter',
    tenantFromReporterNamed: 'From the reporter ({{tenant}})',
    noTenants: 'This customer has no tenants.',
    receivedVia: 'Received via',
    via: {
      phone: 'Phone',
      email: 'Email',
      teams: 'Microsoft Teams',
      in_person: 'In person',
      other: 'Other',
    },
    receivedAt: 'Received at',
    receivedAtHelp: 'Your local time ({{zone}}).',
    product: 'Product',
    component: 'Component',
    noComponent: 'No component',
    type: 'Type',
    priority: 'Priority',
    slaHint: '{{policy}}: first response {{fr}}, resolution {{res}}{{calendar}}',
    slaHintCalendar: ' · {{calendar}}',
    slaHintDefault: 'The SLA clock starts when the ticket is created; the policy is chosen by priority, customer and product.',
    subject: 'Subject',
    subjectPlaceholder: 'One line a colleague would understand at a glance',
    description: 'Description',
    descriptionPlaceholderAgent: 'What the caller described, in their words: what happened, when, which records, and what they already tried.',
    descriptionPlaceholderCustomer: 'What happened, when, which records are affected, and what you already tried.',
    gxpImpact: 'GxP impact',
    gxp: {
      unassessed: 'Not assessed yet',
      none: 'None',
      assessed_no_risk: 'Assessed · no product risk',
      yes: 'Yes — GxP impact',
    },
    gxpNote: 'GxP assessment note',
    gxpNotePlaceholder: 'Why: which records, product or patients could be affected, and who concurred.',
    formRef: 'Form ref',
    formRefPlaceholder: 'FORM-REL-03',
    formVersion: 'Form version',
    formVersionPlaceholder: 'v5.2',
    recordType: 'Linked record type',
    recordTypeNone: 'No linked record',
    recordTypeOther: 'Other (free text)',
    recordTypeCustom: 'Record type',
    recordKey: 'Record key',
    recordKeyValid: 'Matches the {{type}} format',
    recordKeyUnchecked: 'No format on file for this type; saved as typed',
    recordKeyInvalid: 'Doesn’t match the {{type}} format ({{example}})',
    assignee: 'Assignee',
    unassigned: 'Unassigned (routing rules may assign it)',
    watchers: 'Watchers',
    watchersHelp: 'Contacts at the customer who get updates.',
    noWatchers: 'No other contacts at this customer.',
    attachmentsHelp: 'Uploaded after the ticket is created; customer-visible files are attached to the description.',
    addFiles: 'Add files',
    removeFile: 'Remove {{name}}',
    internalOnly: 'Internal only',
    note: 'Internal note',
    notePlaceholder: 'Context for the team: call-back number, what you already checked. Use @Name to mention an agent.',
    noteHelp: 'Never shown to the customer.',
    summary: 'On submit',
    willRun: {
      create: 'Create the ticket',
      attachments: 'Upload {{count}} file(s)',
      note: 'Post the internal note',
    },
    submit: 'Create ticket',
    submitting: 'Working…',
    cancel: 'Cancel',
    progressTitle: 'Progress',
    step: {
      pending: 'Waiting',
      running: 'In progress',
      done: 'Done',
      failed: 'Failed',
    },
    createFailed: 'The ticket was not created: {{message}}',
    partial: '{{key}} was created, but some steps failed. Fix them from the ticket.',
    openTicket: 'Open {{key}}',
    validationSummary: 'Check the highlighted fields.',
    errors: {
      required: 'Required.',
      email: 'Enter a valid email address.',
      future: 'That is in the future.',
      invalid_date: 'Enter a date and time.',
      too_long: 'Too long.',
      record_key: 'This key doesn’t match the record type’s format.',
      gxp_note: 'Explain the assessment.',
      form_version: 'Add the form ref too.',
    },
    pending: {
      title: '{{count}} attachment(s) did not upload',
      item: '{{name}} · {{error}}',
      retry: 'Retry upload',
      retrying: 'Uploading…',
    },
  },
  ticket: {
    eyebrow: 'Ticket',
    title: 'Ticket {{id}}',
    opened: 'Opened {{time}} by {{name}}',
    openedWithRole: 'Opened {{time}} by {{name}}, {{role}}',
    resolvedBy: 'resolved {{time}} by {{name}}',
    productType: '{{product}} · {{type}}',
    followUpOf: 'Follow-up of {{key}}',
    duplicateOf: 'Duplicate of {{key}}',
    notFoundTitle: 'Ticket not found',
    notFound: 'Ticket {{id}} was not found, or you do not have access to it.',
    backToQueue: 'Back to the queue',
    loadError: 'The ticket could not be loaded: {{message}}',
    actions: {
      escalate: 'Escalate to deviation',
      resolve: 'Resolve',
      export: 'Export thread (PDF)',
      exporting: 'Exporting…',
      reopen: 'Reopen',
    },
    tabs: {
      conversation: 'Conversation',
      full: 'Full thread',
      customer: 'Customer replies only',
      notes: 'Internal notes',
      audit: 'Audit trail',
      attachments: 'Attachments',
      withCount: '{{label}} · {{count}}',
    },
    legend: {
      customer: 'Customer',
      agent: 'Title21 agent',
      note: 'Internal note · hidden from customer',
      system: 'System event',
      systemSample: '00:00',
    },
    message: {
      via: 'via {{channel}}',
      channel: { portal: 'portal', email: 'email' },
      firstResponse: 'first response in {{duration}}',
      edited: 'edited',
      internalHeader: 'Internal · not visible to customer',
      download: 'Download {{name}}',
      empty: 'No messages yet.',
      emptyNotes: 'No internal notes yet.',
      loadError: 'Messages could not be loaded: {{message}}',
    },
    resolvedBlock: {
      heading: 'Resolved',
      by: 'Resolved by',
      reason: 'Reason',
      when: 'Date & time',
      category: 'Category: {{category}}',
      rootCauseRecorded: 'Root cause recorded',
      deviation: 'Deviation: {{decision}}',
      summary: 'Resolution summary',
    },
    csat: {
      rated: '{{name}} rated this ticket {{rating}} / 5',
      someone: 'The customer',
    },
    closed: {
      resolved: 'This ticket is resolved. A customer reply within 7 days reopens it automatically.',
      closed: 'This ticket is closed. A new reply opens a linked follow-up ticket.',
      addNote: 'Add internal note',
      reply: 'Reply',
    },
    quick: {
      moveTo: 'Move to',
      priority: 'Priority',
      changePriority: 'Change priority',
      resolveHint: 'Opens Resolve: reason and GxP assessment',
      reopenHint: 'Opens Reopen: why it goes back to work',
      blocked: {
        closed_by_system: 'Closed automatically once the reopen window passes',
        terminal: 'A closed ticket cannot change; a new reply opens a follow-up',
        not_from_here: 'Not reachable from {{from}}',
        reopen_first: 'Reopen it first; it goes back to In progress',
        no_permission: 'Your role cannot make this change',
      },
    },
    rail: {
      linkedRecord: 'Linked record · {{product}}',
      linkedRecordPlain: 'Linked record',
      noRecord: 'No record linked.',
      openInProduct: 'Open in product',
      asOf: 'as of {{time}}',
      details: 'Details',
      status: 'Status',
      editStatus: 'Change status',
      receivedVia: 'Received via',
      gxpNote: 'GxP note',
      assignee: 'Assignee',
      customer: 'Customer',
      tenant: 'Tenant',
      noTenant: 'No tenant',
      product: 'Product',
      formVersion: 'Form / version',
      gxpImpact: 'GxP impact',
      sla: 'SLA',
      slaWithPolicy: 'SLA ({{priority}}, {{target}})',
      deviation: 'Deviation',
      noneLinked: 'None linked',
      unassigned: 'Unassigned',
      followUp: 'Follow up',
      editFollowUp: 'Set the follow-up date',
      clearFollowUp: 'Clear',
      noFollowUp: 'No follow-up set',
      followUpHelp: 'Back on your list then; a scheduled ticket returns to In progress.',
      reminderSent: 'Reminder sent {{when}}',
      noResponse: 'No reply since {{when}} — resolve or chase',
      remaining: '{{time}} remaining',
      overdue: '{{time}} overdue',
      met: 'Met · {{time}} to restore',
      pausedLeft: 'Paused · {{time}} left',
      pausedOnly: 'Paused',
      editAssignee: 'Change assignee',
      editGxp: 'Change GxP impact',
      editTenant: 'Change tenant',
      conflict: 'Someone else changed this ticket. It has been refreshed; check the values and try again.',
      saveError: 'The change was not saved: {{message}}',
    },
    gxp: {
      unassessed: 'Not assessed',
      none: 'None',
      assessed_no_risk: 'Yes · assessed, no product risk',
      yes: 'Yes — GxP impact',
    },
    deviation: {
      not_required: 'Not required',
      raised: 'Raised',
      pending: 'Pending decision',
      raisedRef: 'Raised · {{ref}}',
    },
    category: {
      configuration_restored: 'Configuration restored',
      code_fix: 'Code fix',
      user_error_training: 'User error / training',
      data_corrected: 'Data corrected',
      vendor_issue: 'Vendor issue',
      duplicate: 'Duplicate',
      other: 'Other',
    },
  },
  composer: {
    replyPlaceholder: 'Reply to {{name}}, or @mention a teammate…',
    replyPlaceholderGeneric: 'Write a reply, or @mention a teammate…',
    customerPlaceholder: 'Write a reply…',
    notePlaceholder: 'Internal note, hidden from the customer. @mention a teammate…',
    attach: 'Attach',
    insertRecord: 'Insert record link',
    internalNote: 'Internal note',
    reply: 'Reply',
    hint: 'Closing requires a reason and a resolution summary',
    send: 'Send',
    sending: 'Sending…',
    addNote: 'Add note',
    uploading: 'Uploading {{name}}…',
    removeFile: 'Remove {{name}}',
    mentionNoMatch: 'No teammate matches',
    mentionList: 'Teammates',
    sendError: 'Not sent: {{message}}',
    reopened: 'The reply reopened the ticket.',
    followUp: 'The ticket was closed, so the reply opened follow-up {{key}}.',
    noteMode: 'Writing an internal note',
  },
  recordLink: {
    title: 'Insert record link',
    description: 'Link a record in any Title21 product. Without a provider it is a plain reference.',
    product: 'Product',
    type: 'Record type',
    typePlaceholder: 'din, capa, sop…',
    key: 'Record key',
    keyPlaceholder: 'W1234 26 004517',
    url: 'URL',
    insertAsText: 'Also insert the key into the message',
    submit: 'Link record',
    submitting: 'Linking…',
    notAvailable: 'Linking is not available yet; the key was inserted into the message.',
  },
  escalate: {
    title: 'Escalate to deviation',
    description: 'Record the deviation decision for this ticket. It is audit-trailed.',
    decision: 'Decision',
    ref: 'Deviation reference',
    refPlaceholder: 'DEV-2026-0042',
    submit: 'Escalate',
    submitting: 'Escalating…',
    refRequired: 'A raised deviation needs its reference.',
  },
  resolve: {
    title: 'Resolve {{key}}',
    description: 'The summary is sent to the customer as the final reply. Everything here is audit-trailed.',
    summary: 'Resolution summary',
    summaryHelp: 'Sent to the customer as the final reply.',
    templateCause: 'Cause',
    templateFix: 'Fix',
    templatePrevention: 'Prevention',
    category: 'Category',
    duplicateOf: 'Duplicate of (ticket number)',
    duplicatePlaceholder: 'TKT-04801',
    rootCause: 'Root cause',
    rootCauseRequired: 'Root cause (required for a defect)',
    gxpImpact: 'GxP impact',
    deviationDecision: 'Deviation decision',
    deviationRef: 'Deviation reference',
    reason: 'Reason',
    reasonPlaceholder: 'Short audited reason, e.g. Verified with reporter',
    submit: 'Resolve ticket',
    submitting: 'Resolving…',
    sections: {
      customer: 'What the customer gets',
      classify: 'What it was',
      compliance: 'Compliance',
      audit: 'Audit',
    },
    gxpHelp: {
      none: 'No GxP relevance',
      assessed_no_risk: 'GxP-relevant, assessed: no product risk',
      yes: 'GxP impact: follow your deviation process',
    },
    auditNote: 'Recorded in the audit trail with your name',
    footerNote: 'The customer is notified and can reply to reopen it.',
    fixFields_one: '{{count}} field needs attention',
    fixFields_other: '{{count}} fields need attention',
    serverProblems: 'The server refused the resolution:',
    duplicateNotFound: 'Ticket {{key}} was not found.',
    errors: {
      summary: 'Write the resolution summary (cause, fix, prevention).',
      category: 'Choose a category.',
      rootCause: 'A defect needs its root cause.',
      gxpImpact: 'Assess the GxP impact.',
      deviationRef: 'A raised deviation needs its reference.',
      duplicateOf: 'Enter the ticket number it duplicates.',
      reason: 'Give a short reason.',
    },
  },
  reopen: {
    title: 'Reopen {{key}}',
    description: 'The ticket returns to In progress and the SLA clock resumes. The reason is audit-trailed.',
    reason: 'Reason',
    reasonPlaceholder: 'Why is this being reopened?',
    submit: 'Reopen ticket',
    submitting: 'Reopening…',
    required: 'Give a reason.',
  },
  exportPdf: {
    title: 'Export thread (PDF)',
    description: 'The customer copy never contains internal notes. The footer carries the audit chain head hash.',
    audienceCustomer: 'Customer copy',
    audienceInternal: 'Internal copy (with notes)',
    submit: 'Download PDF',
    thread: 'Thread',
    resolution: 'Resolution',
    attachments: 'Attachments',
    internalNote: 'INTERNAL NOTE',
    resolvedBy: 'Resolved by',
    resolvedAt: 'Resolved at',
    category: 'Category',
    rootCause: 'Root cause',
    deviation: 'Deviation decision',
    reason: 'Reason',
    summary: 'Summary',
    audience: 'Audience: {{audience}}',
    audienceName: { customer: 'customer', internal: 'internal' },
    auditHead: 'Audit chain head {{hash}} ({{state}})',
    verified: 'verified',
    unverified: 'NOT verified',
    generated: 'Generated {{at}} by {{by}}',
    opened: 'Opened {{when}} by {{by}}',
  },
  audit: {
    verify: 'Verify chain',
    verifying: 'Verifying…',
    valid: 'Chain intact · {{length}} entries · head {{hash}}',
    anchored: 'Anchored {{day}}',
    invalid: 'Chain broken at entry {{seq}}: {{reason}}',
    columns: { seq: '#', at: 'When', event: 'Event', field: 'Field', change: 'Change', actor: 'Actor', hash: 'Hash' },
    empty: 'No audit entries.',
    internal: 'internal',
    loadError: 'The audit trail could not be loaded: {{message}}',
    intro: 'Every change to this ticket, in order, signed into a tamper-evident chain.',
    filters: { all: 'All', status: 'Status', messages: 'Messages', files: 'Files', changes: 'Changes' },
    showSystem: 'Show system events ({{count}})',
    nothingHere: 'Nothing in this filter.',
    details: 'Details',
    hideDetails: 'Hide details',
    detail: { seq: 'Entry', hash: 'Hash', prevHash: 'Previous hash', actorId: 'Actor id', field: 'Field', old: 'Before', new: 'After', reason: 'Reason', metadata: 'Context' },
    customerVisible: 'Customer can see',
    event: {
      ticket_created: 'Ticket created ({{channel}})',
      status_change: 'Status: {{from}} → {{to}}',
      status_auto_resume: 'Back to {{to}}: the customer replied',
      status_follow_up_due: 'Back to {{to}}: the follow-up date arrived',
      field_change: '{{field}}: {{from}} → {{to}}',
      message_agent_reply: 'Replied to the customer',
      message_customer_reply: 'Customer replied',
      message_internal_note: 'Internal note added',
      message_system_event: 'System message added',
      message_other: 'Message added',
      message_edited: 'Message edited',
      mentioned: 'Teammates mentioned',
      pending_reminder: 'Reminder sent: still waiting for the customer’s reply',
      no_response_flagged: 'Flagged to the owner: no reply after the reminder',
      follow_up_due: 'Follow-up date reached',
      follow_up_created: 'Follow-up ticket opened from a reply',
      attachment_added: 'File added: {{name}}',
      attachment_downloaded: 'File downloaded: {{name}}',
      attachment_previewed: 'File viewed: {{name}}',
      thread_exported: 'Conversation exported',
      resolved: 'Resolved',
      reopened: 'Reopened',
      auto_reopened: 'Reopened by a customer reply',
      auto_closed: 'Closed automatically (reopen window passed)',
      record_linked: 'Record linked',
      deviation_escalated: 'Escalated to a deviation',
      routed: 'Routed by a rule',
      first_response: 'First response recorded',
      sla_recomputed: 'SLA recalculated',
      sla_warning: 'SLA warning reached',
      sla_breached: 'SLA breached',
    },
    field: {
      assignee: 'Assignee',
      team: 'Team',
      tenant: 'Tenant',
      environment: 'Environment',
      component: 'Component',
      gxpImpact: 'GxP impact',
      gxpNote: 'GxP note',
      priority: 'Priority',
      subject: 'Subject',
      type: 'Type',
      followUp: 'Follow-up date',
      form: 'Form',
      formVersion: 'Form version',
      watchers: 'Watchers',
    },
  },
  attachmentsTab: {
    empty: 'No attachments.',
    columns: { name: 'File', size: 'Size', visibility: 'Visibility', uploaded: 'Uploaded' },
    internal: 'Internal',
    customer: 'Customer',
  },
  analytics: {
    eyebrow: 'Admin · All customers · All products',
    title: 'Support analytics',
    // Shown until the analytics phase (P8) wires the page to the API.
    comingTitle: 'Analytics are on the way',
    coming: 'SLA attainment, first-response times and volume by product and customer will appear here in an upcoming release.',
  },
  sla: { eyebrow: 'Admin', title: 'SLA policies & routing' },
  boards: {
    eyebrow: 'Tickets · Boards',
    title: 'Boards',
    new: 'New board',
    empty: 'No boards yet. Create one to track tickets as cards.',
    emptyTitle: 'Track work as cards',
    mine: 'Mine',
    sharedBy: 'Shared by {{name}}',
    queueBoard: 'Queue · drag a card to change its status or owner; open one to reply',
    private: 'Private',
    shared: 'Shared',
    open: 'Open board',
    groupBy: 'Group by',
    groups: { status: 'Status', assignee: 'Assignee', priority: 'Priority' },
    unassigned: 'Unassigned',
    edit: 'Edit board',
    delete: 'Delete board',
    star: 'Star {{name}}',
    unstar: 'Unstar {{name}}',
    duplicate: 'Duplicate',
    duplicated: 'Copied to {{name}}',
    archive: 'Archive',
    archived: '{{name}} archived',
    restore: 'Restore',
    restored: '{{name}} restored',
    archivedBanner: 'This board is archived. It is out of the lists and the sidebar until you restore it.',
    more: 'More for {{name}}',
    moveToFolder: 'Move to folder',
    myFolders: 'My folders',
    teamFolders: '{{team}} folders',
    noFolder: 'Not in a folder',
    movedTo: '{{name}} moved to {{folder}}',
    removedFrom: '{{name}} taken out of {{folder}}',
    takeOutOf: 'Take out of {{folder}}',
    teamNeedsShared: 'Share the board with your team to put it in a team folder',
    search: 'Search boards',
    noMatch: 'No boards match “{{q}}”.',
    showArchived: 'Archived',
    archivedTitle: 'Archived boards',
    archivedEmpty: 'Nothing archived.',
    backToBoards: 'All boards',
    recent: 'Recent',
    starredTitle: 'Starred',
    unfiled: 'Not in a folder',
    count_one: '{{count}} board',
    count_other: '{{count}} boards',
    wipOver: 'Over the limit of {{limit}}',
    wipAt: 'At the limit of {{limit}}',
    folders: {
      new: 'New folder',
      newMine: 'My folder',
      newMineHelp: 'Only you see it',
      newTeam: 'Team folder',
      newTeamHelp: 'Everyone in {{team}} sees and arranges it',
      noTeam: 'You are in no team yet',
      createTitle: 'New folder',
      renameTitle: 'Rename {{name}}',
      name: 'Name',
      namePlaceholder: 'e.g. Release work',
      scope: 'Who sees it',
      create: 'Create folder',
      rename: 'Rename',
      delete: 'Delete folder',
      deleteTitle: 'Delete {{name}}?',
      deleteBody: 'The folder goes; its boards stay and move to “Not in a folder”.',
      menu: 'Folder actions for {{name}}',
      team: 'Team',
      empty: 'Empty. Use a board’s menu to move it here.',
      moveUp: 'Move {{name}} up',
      moveDown: 'Move {{name}} down',
    },
    deleteTitle: 'Delete {{name}}?',
    deleteBody: 'Only the board goes; its tickets are not touched.',
    truncated: 'Showing the first {{shown}} of {{total}} tickets. Narrow the board’s filters to see the rest.',
    cardsEmpty: 'No tickets',
    dropHere: 'Drop here',
    notAllowed: 'Not allowed from this status',
    moved: '{{key}} moved to {{to}}',
    moveFailed: 'Could not move {{key}}: {{message}}',
    readOnlyGrouping: 'Only the owner can change the saved grouping; this view changes for you only.',
    collapse: 'Collapse {{name}}',
    expand: 'Expand {{name}}',
    atRiskCount_one: '{{count}} ticket at SLA risk',
    atRiskCount_other: '{{count}} tickets at SLA risk',
    filters: {
      label: 'Filter this board',
      search: 'Search cards',
      me: 'Me',
      atRisk: 'SLA at risk',
      customer: 'Customer',
      allCustomers: 'All customers',
      clear: 'Clear',
      count_one: '{{count}} ticket',
      count_other: '{{count}} tickets',
      showing: '{{shown}} of {{total}}',
    },
    view: { label: 'View', board: 'Board', list: 'List', compact: 'Compact cards' },
    list: { subject: 'Subject', customer: 'Customer', status: 'Status', assignee: 'Assignee' },
    bulk: {
      label: 'Actions for {{count}} selected tickets',
      select: 'Select {{key}}',
      selected_one: '{{count}} selected',
      selected_other: '{{count}} selected',
      noCommonMove: 'No common move',
      clear: 'Clear',
      done_one: '{{count}} ticket updated',
      done_other: '{{count}} tickets updated',
      partial: '{{done}} updated; not changed: {{failed}}',
    },
    panel: { openFull: 'Open full page', close: 'Close' },
    card: {
      actions: 'Actions for {{key}}',
      moveTo: 'Move to',
      priority: 'Priority',
      assign: 'Assign to',
      unassign: 'Unassign',
      open: 'Open ticket',
      me: 'Me',
      assignedTo: 'Assigned to {{name}} · click to change',
      changeAssignee: '{{key}} is assigned to {{name}}; change assignee',
      assignKey: 'Assign {{key}}',
      findPerson: 'Find a person…',
      noPeople: 'No one matches',
    },
    dialog: {
      createTitle: 'New board',
      editTitle: 'Edit {{name}}',
      name: 'Name',
      namePlaceholder: 'e.g. CGT release blockers',
      groupBy: 'Group cards by',
      filters: 'Which tickets',
      filtersHelp: 'Nothing ticked means all. Everyone only sees the tickets their own role allows.',
      products: 'Products',
      priorities: 'Priorities',
      assignee: 'Assignee',
      anyAssignee: 'Anyone',
      search: 'Text',
      searchPlaceholder: 'Subject, customer, record…',
      sharing: 'Who can see it',
      private: 'Only me',
      shared: 'Shared',
      team: 'Team',
      noTeam: 'No team',
      members: 'People',
      create: 'Create board',
      save: 'Save',
      description: 'Description',
      descriptionPlaceholder: 'What this board is for (optional)',
      color: 'Colour',
      noColor: 'No colour',
      icon: 'Icon',
      wip: 'Work-in-progress limits',
      wipHelp: 'Per status column; over the limit warns, never blocks',
      wipInvalid: 'A limit is a whole number from 1 to 999.',
    },
    colors: {
      slate: 'Slate',
      blue: 'Blue',
      teal: 'Teal',
      green: 'Green',
      amber: 'Amber',
      orange: 'Orange',
      red: 'Red',
      purple: 'Purple',
      pink: 'Pink',
    },
    icons: {
      kanban: 'Board',
      rocket: 'Rocket',
      flask: 'Flask',
      shield: 'Shield',
      bug: 'Bug',
      clipboard: 'Checklist',
      wrench: 'Wrench',
      truck: 'Truck',
      users: 'People',
      star: 'Star',
      flag: 'Flag',
      zap: 'Lightning',
    },
  },
  access: {
    eyebrow: 'Admin · Access',
    title: 'Users & roles',
    tabs: { users: 'Users', roles: 'Roles', teams: 'Teams' },
    kinds: { AGENT: 'Agent', CUSTOMER: 'Customer user' },
    kindsPlural: { AGENT: 'Agents', CUSTOMER: 'Customer users' },
    errors: {
      required: 'Required.',
      email: 'Enter a valid email address.',
      role: 'Choose a role.',
      customer: 'Choose the customer.',
      slug: 'Lower-case letters, digits and _, starting with a letter (2–48).',
    },
    users: {
      new: 'New user',
      search: 'Search name or email',
      allKinds: 'All users',
      allStatuses: 'Any status',
      active: 'Active',
      disabled: 'Disabled',
      empty: 'No users match.',
      cols: { name: 'Name', kind: 'Type', role: 'Role', where: 'Team / customer', status: 'Status' },
      status: { active: 'Active', invited: 'Invited', disabled: 'Disabled' },
      superAdmin: 'Superadmin',
      you: 'You',
      edit: 'Edit',
      disable: 'Disable',
      enable: 'Enable',
      resend: 'Resend invite',
      createTitle: 'New user',
      editTitle: 'Edit {{name}}',
      fields: {
        kind: 'Type',
        email: 'Email',
        emailHelp: 'Their sign-in name; it cannot change later.',
        firstName: 'First name',
        lastName: 'Last name',
        title: 'Job title',
        titleHelp: 'Shown next to their name, e.g. "Tier 2 · Validated env."',
        role: 'Role',
        roleHelp: 'Only roles you could grant yourself are listed.',
        noRole: 'Choose a role…',
        team: 'Team',
        noTeam: 'No team',
        tier: 'Tier',
        noTier: 'No tier',
        customer: 'Customer',
        noCustomer: 'Choose the customer…',
        tenant: 'Tenant',
        noTenant: 'Whole customer',
        superAdmin: 'Superadmin — every permission, every product and customer',
      },
      create: 'Create and invite',
      save: 'Save',
      invite: {
        sent: 'Invite sent to {{email}}.',
        exists: '{{email}} already has a sign-in; they can use it right away.',
        not_configured: 'User created. No sign-in service is configured here, so they sign in with the dev login.',
      },
      disableTitle: 'Disable {{name}}?',
      disableBody: 'They are signed out and refused on their next request. Their tickets and history stay. You can enable them again.',
    },
    roles: {
      new: 'New role',
      empty: 'No roles yet.',
      builtIn: 'Built-in',
      custom: 'Custom',
      fullAccess: 'Every permission',
      users_one: '{{count}} user',
      users_other: '{{count}} users',
      permissions_one: '{{count}} permission',
      permissions_other: '{{count}} permissions',
      scoped: 'Limited scope',
      edit: 'Edit',
      view: 'View',
      delete: 'Delete',
      createTitle: 'New role',
      editTitle: 'Edit {{name}}',
      viewTitle: '{{name}} (built-in)',
      readOnly: 'Built-in roles are read-only. Create a custom role to change these permissions.',
      fields: {
        name: 'Name',
        slug: 'Key',
        slugHelp: 'Used in the audit trail; it cannot change later.',
        kind: 'For',
        customerWide: 'Sees every ticket of their customer (not only their own)',
        permissions: 'Permissions',
        notGrantable: 'You do not hold this permission, so you cannot grant it.',
        scope: 'Scope',
        scopeHelp: 'Limit what agents with this role see. Nothing ticked means everything.',
        products: 'Products',
        customers: 'Customers',
        allProducts: 'All products',
        allCustomers: 'All customers',
      },
      selectGroup: 'Select all {{group}}',
      create: 'Create role',
      save: 'Save',
      deleteTitle: 'Delete {{name}}?',
      deleteBody: 'Nobody has this role. The change is recorded in the admin audit.',
      inUse: 'Move its users to another role before deleting it.',
    },
    teams: {
      new: 'Add team',
      namePlaceholder: 'Team name',
      empty: 'No teams yet.',
      members_one: '{{count}} member',
      members_other: '{{count}} members',
      tickets_one: '{{count}} ticket',
      tickets_other: '{{count}} tickets',
      rename: 'Rename',
      save: 'Save',
      cancel: 'Cancel',
      delete: 'Delete',
      deleteTitle: 'Delete {{name}}?',
      deleteBody: 'The team has no members and no tickets.',
      inUse: 'Teams with members or tickets can be renamed, not deleted.',
    },
  },
  catalog: {
    eyebrow: 'Admin · Catalog',
    superAdminOnly: 'Only superadmins can manage the catalog.',
    back: 'Back',
    status: { active: 'Active', inactive: 'Inactive', retired: 'Retired', suspended: 'Suspended' },
    kinds: { production: 'Production', validation: 'Validation', test: 'Test', training: 'Training', sandbox: 'Sandbox' },
    normalizers: { none: 'None (trim only)', upper: 'Upper-case', strip_spaces_upper: 'Strip spaces & dashes, upper-case' },
    retire: 'Retire',
    reactivate: 'Reactivate',
    deactivate: 'Deactivate',
    activate: 'Activate',
    rename: 'Rename',
    validated: 'Validated',
    notValidated: 'Not validated',
    products: {
      title: 'Products',
      new: 'New product',
      createTitle: 'New product',
      empty: 'No products yet.',
      cols: { name: 'Name', key: 'Key', components: 'Components', sla: 'SLA target', tickets: 'Tickets', status: 'Status' },
      tabs: { details: 'Details', components: 'Components', patterns: 'Record key patterns' },
      name: 'Name',
      key: 'Key',
      keyHelp: 'Lower-case, digits and _ — used in URLs and routing rules.',
      keyLocked: 'Locked: tickets reference this product.',
      description: 'Description',
      slaTarget: 'SLA target (%)',
      active: 'Active — offered when creating tickets',
      inactiveNote: 'Inactive products are hidden from ticket pickers; existing tickets keep them.',
      saved: 'Saved',
      ticketCount: '{{count}} tickets',
    },
    components: {
      add: 'Add component',
      namePlaceholder: 'Component name, e.g. Release labels',
      keyPlaceholder: 'key',
      empty: 'No components yet.',
      cols: { name: 'Name', key: 'Key', tickets: 'Tickets', status: 'Status' },
      deleteTitle: 'Delete component {{name}}?',
      deleteBody: 'Only components no ticket uses can be deleted. Recorded in the admin audit.',
    },
    patterns: {
      add: 'New pattern',
      createTitle: 'New record key pattern',
      editTitle: 'Edit record key pattern',
      empty: 'No record key patterns yet.',
      cols: { type: 'Type', regex: 'Regex', normalizer: 'Normalizer', url: 'URL template' },
      type: 'Record type',
      typeHelp: 'e.g. din, capa, sop',
      regex: 'Regular expression',
      sample: 'Sample key (must match)',
      normalizer: 'Normalizer',
      urlTemplate: 'URL template (optional)',
      urlHelp: 'https URL with {key}, e.g. https://cgt.example/records/din/{key}',
      tester: 'Live tester',
      testKey: 'Try a key',
      matches: 'Matches',
      noMatch: 'Does not match',
      badRegex: 'Regex does not compile: {{message}}',
      normalized: 'Normalized (search key)',
      url: 'Record URL',
      noUrl: 'No URL template',
      deleteTitle: 'Delete the {{name}} pattern?',
      deleteBody: 'Linked records on tickets keep their keys. Recorded in the admin audit.',
    },
    clients: {
      title: 'Clients & tenants',
      new: 'New client',
      createTitle: 'New client',
      search: 'Search clients…',
      empty: 'No clients match.',
      cols: { name: 'Name', slug: 'Slug', products: 'Products', timeZone: 'Time zone', tenants: 'Tenants', environments: 'Environments', status: 'Status' },
      product: 'Product',
      allProducts: 'All products',
      noProduct: 'No product yet',
      productOption: '{{name}} ({{count}})',
      grouping: 'Show clients',
      view: { list: 'List', byProduct: 'By product' },
      groupCount_one: '{{count}} client',
      groupCount_other: '{{count}} clients',
      tabs: { details: 'Details', tenants: 'Tenants', environments: 'Environments', 'api-access': 'API access' },
      name: 'Name',
      slug: 'Slug',
      slugHelp: 'Lower-case letters, digits and -',
      timeZone: 'Time zone',
      timeZoneHelp: 'IANA zone, e.g. America/Chicago. Business-hours SLAs run in it.',
      emailDomains: 'Email domains',
      emailDomainsHelp: 'Inbound email from these domains is matched to this client. Press Enter or comma to add.',
      domainPlaceholder: 'mayo.edu',
      invalidDomains: 'Not a domain: {{list}}',
      removeDomain: 'Remove {{domain}}',
      suspend: 'Suspend client',
      reactivate: 'Reactivate client',
      suspendTitle: 'Suspend {{name}}?',
      suspendBody: 'A suspended client cannot open new tickets. Existing tickets stay workable by agents.',
      reactivateTitle: 'Reactivate {{name}}?',
      reactivateBody: 'The client can open new tickets again.',
      reason: 'Reason (audited)',
      suspendedNote: 'Suspended: no new tickets.',
    },
    environments: {
      add: 'New environment',
      createTitle: 'New environment',
      editTitle: 'Edit environment',
      empty: 'No environments yet.',
      cols: { product: 'Product', name: 'Name', kind: 'Kind', validated: 'Validated', baseUrl: 'Base URL', tenants: 'Tenants', status: 'Status' },
      product: 'Product',
      productHelp: 'Only active products.',
      name: 'Name',
      kind: 'Kind',
      isValidated: 'Validated (GxP) environment',
      baseUrl: 'Base URL (optional)',
      retireTitle: 'Retire environment {{name}}?',
      retireBody: 'Retired environments are hidden from ticket pickers; tickets and tenant links keep them.',
      deleteTitle: 'Delete environment {{name}}?',
      deleteBody: 'Only environments nothing references can be deleted. Recorded in the admin audit.',
    },
    tenants: {
      add: 'New tenant',
      createTitle: 'New tenant',
      editTitle: 'Edit tenant',
      empty: 'No tenants yet. A tenant is a branch or site of the client.',
      cols: { name: 'Name', code: 'Code', timeZone: 'Time zone', location: 'Location', environments: 'Environments (tenant id)', status: 'Status' },
      name: 'Name',
      code: 'Code',
      codeHelp: 'Short upper-case code, unique for the client (ROC, JAX).',
      timeZone: 'Time zone',
      location: 'Location (optional)',
      links: 'Environment links',
      linksHelp: "Which of the client's environments this tenant uses, and its tenant id inside each.",
      addLink: 'Add environment',
      removeLink: 'Remove link',
      environment: 'Environment',
      externalTenantId: 'Tenant id in the product',
      noEnvironments: 'This client has no environments yet — add them on the Environments tab.',
      retireTitle: 'Retire tenant {{name}}?',
      retireBody: 'Retired tenants are hidden from pickers; tickets, contacts and users keep them.',
      deleteTitle: 'Delete tenant {{name}}?',
      deleteBody: 'Only tenants nothing references can be deleted. Recorded in the admin audit.',
    },
    apiAccess: {
      intro: 'One API key per product environment. Every call names its tenant; only the tenants listed here are accepted.',
      noEnvironments: 'This client has no environments yet — add them on the Environments tab.',
      noKey: 'No API key yet',
      noKeyHelp: 'Issue a key, then paste it into the product (CGT → Integrations → Title21 Smart Support).',
      createKey: 'Create API key',
      createTitle: 'New API key for {{env}}',
      keyName: 'Name',
      scopes: 'Scopes',
      scope: {
        'tickets.read': 'Read tickets and replies',
        'tickets.write': 'Raise tickets and reply',
        'webhooks.manage': 'Register webhooks',
      },
      defaultContact: 'Default contact',
      defaultContactHelp: 'This client has no contacts yet. Who should Title21 reach when a call names no one?',
      contactName: 'Name',
      contactEmail: 'Email',
      clientId: 'Client ID',
      role: { active: 'Active', replacement: 'Replacement', disabled: 'Disabled' },
      lastUsed: 'Last used {{when}}',
      neverUsed: 'Never used',
      webhook: {
        ok: 'Webhook delivering',
        failing_one: 'Webhook failing — {{count}} in 24 h',
        failing_other: 'Webhook failing — {{count}} in 24 h',
        none: 'No webhook yet',
      },
      rotate: 'Rotate',
      disable: 'Disable',
      enable: 'Enable',
      deliveries: 'Deliveries',
      rotatingNote: 'Rotation in progress: both keys work. Disable the old one once the product uses the replacement.',
      disableTitle: 'Disable {{name}}?',
      disableBody: 'Every tenant using this key stops reaching Smart Support immediately.',
      rotateTitle: 'Rotate {{name}}?',
      rotateBody: 'A replacement key is issued; the current one keeps working until you disable it.',
      issuedTitle: 'Copy this key now',
      issuedWarning: 'The secret is shown only once. Paste the CGT API Key value into CGT → Integrations → Title21 Smart Support.',
      baseUrl: 'Base API URL',
      clientSecret: 'Client secret',
      noSecret: 'None — local dev client',
      cgtValue: 'CGT API Key field',
      copy: 'Copy',
      copied: 'Copied',
      confirmCopied: "I've copied it",
      done: 'Done',
      tenants: 'Tenants of this client',
      tenantCols: { tenant: 'Tenant', externalId: 'CGT tenant id', connection: 'Connection', open: 'Open tickets' },
      notLinked: 'Not linked',
      noTenants: 'No tenant uses this environment yet.',
      editTenants: 'Edit tenants',
      connection: { never: 'Not connected yet', connected: 'Last request {{when}}', idle: 'Last request {{when}}' },
      ago: {
        now: 'just now',
        minutes_one: '{{count}} min ago',
        minutes_other: '{{count}} min ago',
        hours_one: '{{count}} h ago',
        hours_other: '{{count}} h ago',
        days_one: '{{count}} day ago',
        days_other: '{{count}} days ago',
      },
      deliveriesTitle: 'Webhook deliveries · {{name}}',
      noDeliveries: 'No deliveries yet.',
      deliveryCols: { event: 'Event', status: 'Status', attempts: 'Attempts', when: 'When', error: 'Last error' },
    },
    errors: {
      required: 'Required.',
      key: 'Lower-case letters, digits and _, starting with a letter.',
      duplicateKey: 'That key is already used.',
      duplicateType: 'This product already has a pattern of that type.',
      percent: 'A whole number between 0 and 100.',
      regex: 'The regular expression does not compile.',
      sampleNoMatch: 'The sample does not match the regex.',
      keyPlaceholder: 'Must contain {key}.',
      https: 'Must be an https URL.',
      slug: 'Lower-case letters, digits and -.',
      timeZone: 'Not a known IANA time zone.',
      code: 'Upper-case letters, digits, _ or - (max 16).',
      linkEnvironment: 'Choose an environment for every row.',
      linkDuplicate: 'An environment is linked twice.',
    },
  },
  admin: {
    tabs: { policies: 'SLA policies', calendars: 'Calendars', routing: 'Routing rules', audit: 'Admin audit' },
    add: { policies: 'New policy', calendars: 'New calendar', routing: 'New rule' },
    editTitle: { policies: 'Edit SLA policy', calendars: 'Edit calendar', routing: 'Edit routing rule' },
    createTitle: { policies: 'New SLA policy', calendars: 'New business calendar', routing: 'New routing rule' },
    empty: 'Nothing here yet.',
    deleteTitle: 'Delete {{name}}?',
    deleteBody: 'This cannot be undone. The change is recorded in the admin audit.',
    active: 'Active',
    inactive: 'Inactive',
    policy: {
      name: 'Name',
      priority: 'Priority',
      firstResponse: 'First response (minutes)',
      resolution: 'Resolution (minutes)',
      calendar: 'Business calendar',
      warnAt: 'Warn at (% of budget)',
      pauseStatuses: 'Clock pauses in',
      customer: 'Customer (optional)',
      product: 'Product (optional)',
      anyCustomer: 'Any customer',
      anyProduct: 'Any product',
      targetsHeader: 'First response / resolution',
      scope: 'Scope',
    },
    calendar: {
      name: 'Name',
      timeZone: 'Time zone',
      timeZoneHelp: 'IANA zone (America/New_York) or "customer" for the customer\'s zone.',
      is24x7: 'Open 24×7',
      hours: 'Business hours',
      hoursHelp: 'Per day, e.g. 09:00-12:00, 13:00-17:00. Empty = closed.',
      holidays: 'Holidays',
      holidaysHelp: 'One YYYY-MM-DD per line.',
      days: { mon: 'Mon', tue: 'Tue', wed: 'Wed', thu: 'Thu', fri: 'Fri', sat: 'Sat', sun: 'Sun' },
      summary24x7: '24×7',
      holidayCount: '{{count}} holidays',
    },
    rule: {
      name: 'Name',
      order: 'Order',
      conditions: 'Conditions (JSON)',
      actions: 'Actions (JSON)',
    },
    auditCols: { at: 'When', entity: 'Entity', action: 'Action', actor: 'Actor', reason: 'Reason' },
    errors: {
      required: 'Required.',
      positiveMinutes: 'A whole number of minutes above 0.',
      firstAfterResolution: 'First response must not exceed resolution.',
      percent: 'A whole percentage between 1 and 99.',
      timeZone: 'Not a known time zone.',
      hours: 'Use HH:MM-HH:MM ranges, separated by commas.',
      noHours: 'Open at least one day, or choose 24×7.',
      holidays: 'Dates must be YYYY-MM-DD.',
      json: 'Must be a JSON object or array.',
      order: 'A whole number.',
    },
  },
  login: {
    title: 'Sign in to Support Desk',
    devSubtitle: 'Development sign-in: no password required.',
    subtitle: 'Use your Title21 account.',
    email: 'Email',
    username: 'Email',
    password: 'Password',
    submit: 'Sign in',
    submitting: 'Signing in…',
    quickPick: 'Quick pick',
    failed: 'Sign-in failed',
    newPasswordRequired: 'Your account needs a new password. Set it in the Title21 sign-in page first.',
  },
} as const
